import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { switchMap } from 'rxjs';
import { ImpactService } from './impact.service';
import { Transport } from 'src/app/trip-calculator/models/transport';
import { TripService } from 'src/app/trip-calculator/services/trip.service';
import { ReductionTip } from '../models/reduction-tip';
import { TripCommitment } from 'src/app/shared/model/reduction-action';
import { applyReductionTips } from '../utils/apply-reduction-tips';
import {
  AlternativeMode,
  getTipSaving,
  pickBestTipPerElement,
  sumSavingsByOverviewType,
} from '../utils/reduction-tip-savings';

/**
 * State of the reduction tips dialog. Every component of the dialog reads it
 * directly rather than receiving it through inputs, so a deeply nested row can
 * select itself without the whole chain forwarding handlers.
 */
@Injectable({
  providedIn: 'root',
})
export class ReductionTipsService {
  private impactService = inject(ImpactService);
  private tripService = inject(TripService);
  private translocoService = inject(TranslocoService);

  tips = signal<ReductionTip[]>([]);
  commitments = signal<TripCommitment[]>([]);
  isLoading = signal(true);
  /** A save is in flight — the apply button must not fire twice. */
  isApplying = signal(false);
  dialogVisible = signal(false);

  /** Tips the user simulates, and the alternative picked for transport tips. */
  selectedTipIds = signal<ReadonlySet<string>>(new Set<string>());
  alternativeModeByTipId = signal<Record<string, AlternativeMode>>({});

  /** Every figure in the dialog is displayed per traveller when true. */
  readonly perPerson = this.impactService.perPerson;

  /** Travellers of the loaded trip — every per-person conversion divides by it. */
  readonly travellers = computed(
    () => this.impactService.trip()?.travellers ?? 1,
  );

  /** kg CO₂e avoided per breakdown category, fed to the simulated donut. */
  readonly savingsByType = computed<Record<string, number>>(() => {
    const selected = this.selectedTipIds();
    const modes = this.alternativeModeByTipId();
    const ctx = {
      travellers: this.travellers(),
      transportCategoryOf: (transportType: string) =>
        this.impactService.getTransportCategory(transportType) as
          | string
          | undefined,
    };

    return sumSavingsByOverviewType(
      this.tips()
        .filter((tip) => selected.has(tip.id))
        .map((tip) => getTipSaving(tip, ctx, modes[tip.id])),
    );
  });

  /** The state is root-scoped, so a new trip starts from a clean simulation. */
  load(tripId: string): void {
    this.isLoading.set(true);
    this.selectedTipIds.set(new Set<string>());
    this.alternativeModeByTipId.set({});
    this.impactService.getReductionTips(tripId).subscribe({
      next: (response) => {
        this.tips.set(response.tips);
        this.commitments.set(response.commitments ?? []);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  openDialog(): void {
    this.dialogVisible.set(true);
  }

  isSelected(tipId: string): boolean {
    return this.selectedTipIds().has(tipId);
  }

  modeOf(tipId: string): AlternativeMode | undefined {
    return this.alternativeModeByTipId()[tipId];
  }

  /** Saving of one tip over the whole trip, on the alternative in use. */
  savingOf(tip: ReductionTip): number {
    return getTipSaving(
      tip,
      { travellers: this.travellers() },
      this.modeOf(tip.id),
    ).savings;
  }

  setSelection(tipIds: readonly string[], selected: boolean): void {
    this.selectedTipIds.update((current) => {
      const next = new Set(current);
      tipIds.forEach((id) => (selected ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  setMode(tipId: string, mode: AlternativeMode): void {
    this.alternativeModeByTipId.update((modes) => ({
      ...modes,
      [tipId]: mode,
    }));
  }

  /**
   * The tips actually written to the trip: the selected ones, arbitrated so a
   * transport carrying two competing tips only takes the better one — the same
   * rule the simulated donut applies.
   */
  private readonly appliedTips = computed(() =>
    pickBestTipPerElement(
      this.tips().filter((tip) => this.isSelected(tip.id)),
      {
        travellers: this.travellers(),
        transportCategoryOf: (transportType: string) =>
          this.impactService.getTransportCategory(transportType) as
            | string
            | undefined,
      },
      this.alternativeModeByTipId(),
    ),
  );

  /**
   * Name given to a leg replaced by an alternative: "Train (alternative to
   * <what it was>)", falling back on the translated transport type when the
   * leg was never named. The mode chosen in the dialog comes first — a trip's
   * transport list is read for what each leg now is, the flight it replaces
   * being the context.
   */
  private alternativeTitleOf(
    transport: Transport,
    mode: AlternativeMode,
  ): string {
    const current =
      transport.title?.trim() ||
      this.translate(`categoryType.${transport.type}`);

    return this.translate('reductionTips.alternativeTitle', {
      mode: this.translate(`reductionTips.transportAlternative.${mode}`),
      title: this.originalTitleOf(current),
    });
  }

  /**
   * The leg's own name, freed from the wording a previous apply wrapped it in
   * — renaming twice must not nest it. Only what surrounds the original name
   * is stable across modes, so it is read off the pattern itself rather than
   * spelled out once per language.
   */
  private originalTitleOf(title: string): string {
    const MODE_SLOT = '\u0000';
    const TITLE_SLOT = '\u0001';
    const parts = this.translate('reductionTips.alternativeTitle', {
      mode: MODE_SLOT,
      title: TITLE_SLOT,
    }).split(TITLE_SLOT);
    const head = parts[0] ?? '';
    const tail = parts[1] ?? '';

    const opening = head.slice(head.indexOf(MODE_SLOT) + MODE_SLOT.length);
    const start = opening ? title.indexOf(opening) : -1;
    if (start <= 0 || !title.endsWith(tail)) return title;

    return title.slice(start + opening.length, title.length - tail.length);
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translocoService.translate<string>(key, params) ?? '';
  }

  /**
   * Rewrites the trip as if the selected tips had been taken, and saves it —
   * `PUT /trips/:id` recalculates the impact server-side.
   *
   * The trip is refetched rather than taken from `impactService.trip()`: that
   * payload carries no flight or road stops, and the update endpoint replaces
   * the collections it receives, so saving it back would wipe every itinerary.
   */
  applyTips(): void {
    const tripId = this.impactService.trip()?.id;
    const tips = this.appliedTips();
    if (!tripId || tips.length === 0 || this.isApplying()) return;

    this.isApplying.set(true);
    this.tripService
      .getTrip(tripId)
      .pipe(
        switchMap((trip) =>
          this.tripService.updateTrip(
            applyReductionTips(
              trip,
              tips,
              this.alternativeModeByTipId(),
              (transport, mode) => this.alternativeTitleOf(transport, mode),
            ),
          ),
        ),
        // Saved figures are stale until the report is read back.
        switchMap(() => this.impactService.getDetailedImpactReport(tripId)),
      )
      .subscribe({
        next: () => {
          this.isApplying.set(false);
          this.dialogVisible.set(false);
          // Saving recreates the trip's rows, so the tips now point at ids
          // that no longer exist — they have to be asked for again.
          this.load(tripId);
        },
        error: () => this.isApplying.set(false),
      });
  }
}
